import { Key, useContext, useEffect, useState } from "react";
import {
  Center,
  Container,
  Flex,
  Title,
  Text,
  Button,
  Grid,
  Group,
  Space,
  Card,
  NumberInput,
  Image,
  Alert
} from "@mantine/core";
import { ValuesContext } from "../../ValuesContext";
import useDocumentTitle from "../../useDocumentTitle";
import { TotalPrice } from "./utils";
import { getConstants } from "../../constants";

const Step4 = (props: {
  productData: any;
  active: number;
  nbNight: number;
  Hotel_id: number;
  Petitdej: any;        // produit "défaut" petit-déj
  Repas: any;           // produit "défaut" repas
  PromoPetitdej: any;   // produit "promo" petit-déj
  PromoRepas: any;      // produit "promo" repas
  handleStepChange(nextStep: number): any;
}) => {
  const constants = getConstants(props.Hotel_id);

  const { values, setValues } = useContext(ValuesContext);
  const [alert, setlAlert] = useState(false);

  const petitdej = props.Petitdej;
  const repas = props.Repas;
  const promoBreakfast = props.PromoPetitdej;
  const promoMeal = props.PromoRepas;

  const [selectedRestauration, setSelectedRestauration] = useState<any>({
    id: values.Restauration.ProductId,
    name: values.Restauration.ProductName,
    price: values.Restauration.ProductPrice,
    chargingMode: values.Restauration.ChargingMode,
    Count: 1
  });
  const [selectedAdditionalProducts, setSelectedAdditionalProducts] = useState<any[]>(
    values.AdditionalProducts
  );

  // -------- Helpers / règles de calcul --------
  const discountPct = Number(values?.Discount) || 0;
  const hasDiscount = discountPct > 0;
  const personsCount =
    (values.Step1.NbCuriste !== "1" || Number(values.Step1.Accompagnant) === 1) ? 2 : 1;

  const stripDiacritics = (s: string) =>
    (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  // quantité d’affichage pour une ligne “promo 0€” (n’influe pas le total)
  const promoCountFor = (prod: any) => {
    switch (prod?.ChargingMode) {
      case "PerPersonPerTimeUnit":
        return personsCount * props.nbNight;
      case "PerPerson":
        return personsCount;
      case "PerTimeUnit":
        return props.nbNight;
      case "Once":
      default:
        return 1;
    }
  };

  // insère/normalise une ligne promo (0€) avec la bonne quantité d’affichage
  const ensureIncluded = (current: any[], prod: any) => {
    if (!prod || !prod.Id) return current;
    const count = promoCountFor(prod);
    const exists = current.some((p) => p.ProductId === prod.Id);
    if (exists) {
      return current.map((p) =>
        p.ProductId === prod.Id
          ? { ...p, price: 0, Count: count }
          : p
      );
    }
    return [
      ...current,
      {
        ProductId: prod.Id,
        ProductName: prod.ExternalName,
        price: 0,
        chargingMode: prod.ChargingMode,
        Count: count
      }
    ];
  };

  // -------- Initialisation : on force le petit-déj “défaut” comme resto choisi --------
  useEffect(() => {
    if (selectedRestauration.id === '' && petitdej && petitdej.Id) {
      const restau = {
        id: petitdej.Id,
        name: petitdej.ExternalName,
        price: petitdej.Price?.Value ?? 0,
        chargingMode: petitdej.ChargingMode,
        Count: 1
      };

      // si promo active, on ajoute la ligne “Remise -20% petit déjeuner” (0€)
      let withIncluded = [...selectedAdditionalProducts];
      if (hasDiscount) {
        withIncluded = ensureIncluded(withIncluded, promoBreakfast);
      }

      setSelectedRestauration(restau);
      updateStep4Values(restau, withIncluded);
      setSelectedAdditionalProducts(withIncluded);
    }

    setValues((prevValues) => ({
      ...prevValues,
      TotalPrice: TotalPrice(values),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoBreakfast, petitdej, setValues, values, personsCount, props.nbNight]);

  // Si #pers / #nuits changent, on recalcule juste la quantité d’affichage des promos déjà présentes
  useEffect(() => {
    if (!hasDiscount) return;
    let next = [...selectedAdditionalProducts];
    if (promoBreakfast?.Id && next.some(p => p.ProductId === promoBreakfast.Id)) {
      next = ensureIncluded(next, promoBreakfast);
    }
    if (promoMeal?.Id && next.some(p => p.ProductId === promoMeal.Id)) {
      next = ensureIncluded(next, promoMeal);
    }
    setSelectedAdditionalProducts(next);
    updateStep4Values(selectedRestauration, next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personsCount, props.nbNight]);

  // Garder la ligne “Remise -20% petit déjeuner” en phase avec la sélection du petit-déj
  useEffect(() => {
    if (!promoBreakfast?.Id) return;

    const isBreakfastSelected = selectedRestauration?.id === petitdej?.Id;

    const hasPromoBF = selectedAdditionalProducts.some(
      (p) => p.ProductId === promoBreakfast.Id
    );
    const shouldHavePromoBF = hasDiscount && isBreakfastSelected;

    const ensurePromoBF = () =>
      ensureIncluded([...selectedAdditionalProducts], promoBreakfast);

    if (shouldHavePromoBF && !hasPromoBF) {
      const next = ensurePromoBF();
      setSelectedAdditionalProducts(next);
      updateStep4Values(selectedRestauration, next);
    } else if (shouldHavePromoBF && hasPromoBF) {
      const next = ensurePromoBF(); // met à jour le Count si besoin
      setSelectedAdditionalProducts(next);
      updateStep4Values(selectedRestauration, next);
    } else if (!shouldHavePromoBF && hasPromoBF) {
      const next = selectedAdditionalProducts.filter(
        (p) => p.ProductId !== promoBreakfast.Id
      );
      setSelectedAdditionalProducts(next);
      updateStep4Values(selectedRestauration, next);
    }
  }, [
    values.Discount,
    selectedRestauration?.id,
    petitdej?.Id,
    promoBreakfast?.Id,
    props.nbNight,
    values.Step1.NbCuriste,
    values.Step1.Accompagnant,
  ]);

  // -------- Calculs de prix (total) --------
  // -------- Calculs de prix (total) --------
  const updateStep4Values = async (item1: any, selectedAdditional: any[]) => {
    let additionalProductsCost = 0;

    const Restauration = {
      ProductId: item1.id,
      ProductName: item1.name,
      ProductPrice: item1.price, // prix catalogue (remise appliquée plus bas si petit-déj)
      ChargingMode: item1.chargingMode,
      Count: 1
    };

    // helper pour savoir si une ligne est un "repas" (repas par défaut ou "repas package")
    const isMealLine = (p: any) => {
      const byId = !!(repas?.Id && p.ProductId === repas.Id);
      const byName =
        (p?.ProductName || "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
          .includes("repas package");
      return byId || byName;
    };

    // produits additionnels (remise appliquée si repas)
    selectedAdditional.forEach((product) => {
      if (!product || product.price === 0) return; // lignes promo à 0€ -> ignorées

      // prix unitaire effectif (remise si repas + promo active)
      let unit = Number(product.price);
      if (hasDiscount && isMealLine(product)) {
        unit = +(unit * (1 - discountPct / 100)).toFixed(2);
      }

      switch (product.chargingMode) {
        case "PerPersonPerTimeUnit":
          additionalProductsCost += unit * personsCount * props.nbNight * product.Count;
          break;
        case "PerTimeUnit":
          additionalProductsCost += unit * props.nbNight * product.Count;
          break;
        case "PerPerson":
          additionalProductsCost += unit * personsCount * product.Count;
          break;
        case "Once":
          additionalProductsCost += unit * product.Count;
          break;
      }
    });

    // Remise sur le petit-déj pour le total si promo active
    const isBreakfastSelected = Restauration.ProductId === (petitdej?.Id ?? "");
    let unitPrice = Number(Restauration.ProductPrice);
    if (hasDiscount && isBreakfastSelected) {
      unitPrice = +(unitPrice * (1 - discountPct / 100)).toFixed(2);
    }

    let prixrestau = 0;
    switch (Restauration.ChargingMode) {
      case "PerPersonPerTimeUnit":
        prixrestau = unitPrice * personsCount * props.nbNight;
        break;
      case "PerTimeUnit":
        prixrestau = unitPrice * props.nbNight;
        break;
      case "PerPerson":
        prixrestau = unitPrice * personsCount;
        break;
      case "Once":
        prixrestau = unitPrice;
        break;
    }

    await setValues((prev) => ({
      ...prev,
      Restauration,
      AdditionalProducts: selectedAdditional,
      // 👇 on additionne bien resto + add-ons (avec remises éventuelles)
      AdditionalProductsPrice: additionalProductsCost + prixrestau,
    }));
  };


  // toggle simple d’un produit additionnel “classique”
  const handleAdditionalProductSelection = (product: any, newProduct: boolean) => {
    let updated = [...selectedAdditionalProducts];

    const existing = updated.find((item) => item.ProductId === product.ProductId);

    if (existing && !newProduct) {
      updated = updated.filter((item) => item.ProductId !== product.ProductId);
    } else if (existing && newProduct) {
      existing.Count = product.Count;
    } else {
      updated.push(product);
    }

    setSelectedAdditionalProducts(updated);
    updateStep4Values(selectedRestauration, updated);
  };

  // Un clic : ajoute/retire le “repas normal” + la ligne promo repas (0€) si promo active
  const addOrToggleRestoWithOptionalPromo = (clicked: any, attachPromoMeal: boolean) => {
    const promo = promoMeal;
    let next = [...selectedAdditionalProducts];

    const clickedIdx = next.findIndex(p => p.ProductId === clicked.Id);
    const promoIdx = promo?.Id ? next.findIndex(p => p.ProductId === promo.Id) : -1;

    if (clickedIdx >= 0) {
      // retirer le repas => retirer aussi la promo
      next.splice(clickedIdx, 1);
      if (promoIdx >= 0) next.splice(promoIdx > clickedIdx ? promoIdx - 1 : promoIdx, 1);
    } else {
      // ajouter le repas (Count=1 — la quantité “réelle” est gérée dans le calcul total)
      next.push({
        ProductId: clicked.Id,
        ProductName: clicked.ExternalName,
        price: clicked.Price.Value,
        chargingMode: clicked.ChargingMode,
        Count: 1,
      });

      // …puis la promo, avec la quantité d’affichage correcte
      if (attachPromoMeal && promo?.Id && hasDiscount) {
        const qty = promoCountFor(promo);
        if (promoIdx >= 0) {
          next[promoIdx] = { ...next[promoIdx], price: 0, Count: qty };
        } else {
          next.push({
            ProductId: promo.Id,
            ProductName: promo.ExternalName,
            price: 0,
            chargingMode: promo.ChargingMode,
            Count: qty,
          });
        }
      }
    }

    setSelectedAdditionalProducts(next);
    updateStep4Values(selectedRestauration, next);
  };

  const getChargingModeText = (chargingMode: string) => {
    switch (chargingMode) {
      case "PerPersonPerTimeUnit":
        return "/ Nuit / Pers";
      case "PerTimeUnit":
        return "/ Nuit";
      case "PerPerson":
        return "/ Pers";
      case "Once":
        return "";
      default:
        return "";
    }
  };

  useDocumentTitle(`Choix des produits - ${values.Hotel.name}`);
  return (
    <div className="select-zone">
      <Center>
        <Title order={3}>Options et Personnalisation</Title>
      </Center>
      <Space h="xl" />
      <Space h="xl" />
      <Container>
        <Title order={2}>RESTAURATION</Title>
      </Container>
      <Space h="xl" />

      {props.productData
        .filter((i: any) =>
          // catégories de restau
          i.AccountingCategoryId === "edc4fec1-bbdd-4fcc-82d3-adb300e3c573" ||
          i.AccountingCategoryId === "1055d3d3-6b62-4dd7-bc5d-ad7400a43e76" ||
          i.AccountingCategoryId === "84d3774e-9dd8-4a5a-a644-ad7400a43e76" ||
          i.AccountingCategoryId === "917b7af5-a423-4446-b26e-adaa00f3da28" ||
          i.AccountingCategoryId === constants.categorie_id ||
          // forcer l’affichage des 4 variantes
          i.Id === petitdej?.Id ||
          i.Id === repas?.Id ||
          i.Id === promoBreakfast?.Id ||
          i.Id === promoMeal?.Id
        )
        .map((product: any, key: Key) => {
          const isPromoBreakfast = !!promoBreakfast?.Id && product.Id === promoBreakfast.Id;
          const isPromoMeal = !!promoMeal?.Id && product.Id === promoMeal.Id;
          const isIncluded = Boolean(isPromoBreakfast || isPromoMeal);

          const isDefaultBreakfast = !!petitdej?.Id && product.Id === petitdej.Id;
          const isDefaultMeal = !!repas?.Id && product.Id === repas.Id;

          const isSelectedRestauration = selectedRestauration?.id === product.Id;
          const isSelectedAdditional = selectedAdditionalProducts.some(p => p.ProductId === product.Id);

          // --- NOUVEAU : si le nom contient "Repas package", on considère que ça doit afficher la remise
          const normalizedName = stripDiacritics(product?.ExternalName || "").toLowerCase();
          const isMealPackageName = normalizedName.includes("repas package");

          const basePrice = Number(product?.Price?.Value ?? product?.Price?.GrossValue ?? 0);

          // Affichage prix : remiser si promo active ET (repas “défaut” OU nom "Repas package")
          const displayedPrice = hasDiscount && (isDefaultMeal || isMealPackageName)
            ? +((basePrice) * (1 - discountPct / 100)).toFixed(2)
            : basePrice;

          return (
            <div key={String(key)}>
              <Space h="xl" />

              <Card
                sx={(theme) => ({
                  backgroundColor:
                    isSelectedRestauration || isIncluded || isSelectedAdditional
                      ? theme.colorScheme === "dark"
                        ? theme.colors.dark[5]
                        : theme.colors.gray[1]
                      : theme.colors.white,
                })}
              >
                <Grid align="center" mx={10}>
                  {product.imageUrl && window.innerWidth >= 800 && (
                    <Grid.Col span={2} md={2} lg={2} pl={"xs"}>
                      <Image maw={100} mx="auto" radius="md" pl={0} src={product.imageUrl} alt={product.ExternalName} />
                    </Grid.Col>
                  )}
                  <Grid.Col
                    span={product.imageUrl && window.innerWidth >= 800 ? 6 : 8}
                    md={10}
                    lg={product.imageUrl && window.innerWidth >= 800 ? 6 : 8}
                  >
                    <Card.Section>
                      <Text className={"main-detail"}>{product.ExternalName}</Text>
                      <Text>{product.Description}</Text>
                    </Card.Section>
                  </Grid.Col>
                  <Grid.Col span={4} md={12} lg={4}>
                    <Card.Section>
                      <Flex align="flex-end" direction="column">
                        <Flex align="center" direction="row" gap="xs">
                          {isIncluded ? (
                            <Text className={"price"}> {" "} Inclus</Text>
                          ) : isDefaultBreakfast ? (
                            // ne pas afficher le prix pour le petit-déj “défaut”
                            null
                          ) : (
                            <>
                              <Text className={"price"}>
                                {Number(displayedPrice).toFixed(2).replace(".", ",")} €
                              </Text>
                              <Text className={"from-price"}>
                                {getChargingModeText(product.ChargingMode)}
                              </Text>
                            </>
                          )}
                        </Flex>
                        <Button
                          className={
                            isIncluded ||
                              (isDefaultBreakfast && isSelectedRestauration) ||
                              isSelectedAdditional
                              ? "night-nav-active"
                              : "night-nav"
                          }
                          onClick={() => {
                            if (isIncluded || isDefaultBreakfast) return; // non cliquable
                            // si ce n’est PAS le petit-déj => attacher la promo repas 0€ si promo active
                            const attachPromo = !isDefaultBreakfast && !!promoMeal?.Id && hasDiscount;
                            addOrToggleRestoWithOptionalPromo(product, attachPromo);
                          }}
                        >
                          {isIncluded
                            ? "Sélectionné"
                            : (isDefaultBreakfast && isSelectedRestauration) || isSelectedAdditional
                              ? "Sélectionné"
                              : "Choisir"}
                        </Button>
                      </Flex>
                    </Card.Section>
                  </Grid.Col>
                </Grid>
              </Card>
            </div>
          );
        })}

      <Space h="xl" />
      <Container>
        <Title order={2}>AMELIOREZ VOTRE SEJOUR</Title>
      </Container>

      {props.productData
        .filter((i: any) =>
          i.AccountingCategoryId !== "edc4fec1-bbdd-4fcc-82d3-adb300e3c573" &&
          i.AccountingCategoryId !== "1055d3d3-6b62-4dd7-bc5d-ad7400a43e76" &&
          i.AccountingCategoryId !== "84d3774e-9dd8-4a5a-a644-ad7400a43e76" &&
          i.AccountingCategoryId !== "917b7af5-a423-4446-b26e-adaa00f3da28" &&
          // retirer ceux déjà affichés
          i.Id !== petitdej?.Id &&
          i.Id !== repas?.Id &&
          i.Id !== promoBreakfast?.Id &&
          i.Id !== promoMeal?.Id &&
          i.AccountingCategoryId !== constants.categorie_id
        )
        .map((product: any, key: Key) => (
          <Card key={key}>
            <Grid align="center" mx={0}>
              {product.imageUrl && window.innerWidth >= 800 && (
                <Grid.Col span={2} md={2} lg={2} pl={"xs"}>
                  <Image maw={100} mx="auto" radius="md" pl={0} src={product.imageUrl} alt={product.ExternalName} />
                </Grid.Col>
              )}

              <Grid.Col
                span={product.imageUrl && window.innerWidth >= 800 ? 7 : 9}
                md={10}
                lg={product.imageUrl && window.innerWidth >= 800 ? 7 : 9}
              >
                <Card.Section mt={10} pl={"md"}>
                  <Text className={"main-detail"}>{product.ExternalName}</Text>
                  <Text>{product.Description}</Text>
                </Card.Section>
              </Grid.Col>
              <Grid.Col span={3} md={12} lg={3}>
                <Card.Section>
                  <Flex align="flex-end" direction="column">
                    <Flex align="center" direction="row" gap="xs">
                      <Text className={"price"}>{product.Price.Value.toFixed(2).replace(".", ",")} €</Text>
                      <Text className={"from-price"}>
                        {getChargingModeText(product.ChargingMode)}
                      </Text>
                      {selectedAdditionalProducts.find(
                        (p) => p.ProductId === product.Id
                      ) && product.ChargingMode !== "PerTimeUnit" && product.ChargingMode !== "PerPersonPerTimeUnit"
                        ?
                        <NumberInput
                          defaultValue={selectedAdditionalProducts.find((p) => p.ProductId === product.Id).Count}
                          max={Number(values.nbNight) * Number(values.Step1.NbCuriste)}
                          min={1}
                          size="sm"
                          style={{ width: '60px' }}
                          inputMode="none"
                          onChange={(newValue) =>
                            handleAdditionalProductSelection({
                              ProductId: product.Id,
                              ProductName: product.ExternalName,
                              price: product.Price.Value,
                              chargingMode: product.ChargingMode,
                              Count: newValue
                            }, true)
                          }
                        />
                        : null}
                    </Flex>
                    <Space h="md" />
                    <Button
                      className={
                        selectedAdditionalProducts.find((p) => p.ProductId === product.Id)
                          ? "night-nav-active"
                          : "night-nav"
                      }
                      onClick={() => {
                        handleAdditionalProductSelection({
                          ProductId: product.Id,
                          ProductName: product.ExternalName,
                          price: product.Price.Value,
                          chargingMode: product.ChargingMode,
                          Count: 1
                        }, false);
                      }}
                      uppercase
                    >
                      {selectedAdditionalProducts.find((p) => p.ProductId === product.Id)
                        ? "Sélectionné"
                        : "Choisir"}
                    </Button>
                  </Flex>
                </Card.Section>
              </Grid.Col>
            </Grid>
          </Card>
        ))
      }

      <Space h="xl" />
      <Space h="xl" />
      {alert && (
        <Alert title="Erreur !" color="red" mb={20}>
          Veuillez choisir les formules de restauration.
        </Alert>
      )}
      <Group position="center" my="xl">
        <Button
          className={"step-btn-previous"}
          onClick={() => props.handleStepChange(props.active - 1)}
        >
          ← Retour
        </Button>

        <Button
          className={"night-nav-active"}
          onClick={() => {
            if (selectedRestauration.id) {
              updateStep4Values(
                selectedRestauration,
                selectedAdditionalProducts
              );
              props.handleStepChange(props.active + 1);
            } else {
              setlAlert(true)
            }
          }}
        >
          Suivant →
        </Button>
      </Group>
    </div >
  );
};

export default Step4;
